import { CATEGORIES, CATEGORY_BY_ID } from "./categories";
import type { Category } from "@/core/data/types";

/**
 * Content Study Library — the compiled "digital book". Entries are tagged by
 * discipline; buildBook() groups them into chapters (by discipline group →
 * discipline) to form a table of contents. Summaries are written in original
 * wording with citations to respect source copyright. (Owner ingestion of books/
 * courses/notes arrives with the Phase 4 Content Studio.)
 */

export interface LibrarySource {
  title: string;
  author?: string;
  url?: string;
}

export interface LibraryEntry {
  id: string;
  disciplineIds: string[];
  title: string;
  summary: string;
  /** Markdown body (rendered by the lightweight Markdown component). */
  body: string;
  sources: LibrarySource[];
  tags: string[];
}

export const LIBRARY_ENTRIES: LibraryEntry[] = [
  {
    id: "lib-core-loops",
    disciplineIds: ["systems"],
    title: "Core Loops",
    summary: "The repeating cycle of action, reward, and motivation that forms the heartbeat of a game.",
    body: `## What it is
A **core loop** is the smallest repeatable cycle of activity a player performs: take an *action*, receive *feedback/reward*, and gain a *reason to do it again*. Most of a player's time is spent inside this loop, so its quality largely determines whether a game feels good.

## Why it matters
If the core loop isn't satisfying in isolation, no amount of content or story will save the game. Designers prototype the loop first and tune it before building outward.

## Designing one
- Identify the single **primary verb** (shoot, build, match, negotiate).
- Make the **feedback** immediate and legible.
- Ensure each loop **feeds the next** — a reward that enables a slightly better next attempt.
- Layer **secondary loops** (session-level, meta-level) around the core.

## Key takeaways
- Prototype the loop before the content.
- A great loop creates a "one more go" pull on its own.`,
    sources: [
      { title: "A Theory of Fun for Game Design", author: "Raph Koster" },
      { title: "Game Design Workshop", author: "Tracy Fullerton" },
    ],
    tags: ["core-loop", "fundamentals"],
  },
  {
    id: "lib-mda",
    disciplineIds: ["systems"],
    title: "The MDA Framework",
    summary: "Mechanics → Dynamics → Aesthetics: a lens connecting what you build to what players feel.",
    body: `## The three layers
- **Mechanics** — the rules and systems you author directly.
- **Dynamics** — the run-time behavior that emerges as players interact with the mechanics.
- **Aesthetics** — the emotional responses evoked in the player.

## Two directions
Designers build left-to-right (mechanics create dynamics create aesthetics), but players experience it right-to-left (they feel aesthetics, driven by dynamics, produced by mechanics). Designing well means reasoning in *both* directions.

## Practical use
Start from a target **aesthetic** ("tense", "social", "discovery"), hypothesize the **dynamics** that produce it, then design the **mechanics** that make those dynamics likely.

## Key takeaways
- Small mechanic changes can swing the aesthetic dramatically.
- Name your target aesthetics — they make trade-offs decidable.`,
    sources: [
      { title: "MDA: A Formal Approach to Game Design and Game Research", author: "Hunicke, LeBlanc & Zubek (2004)" },
    ],
    tags: ["framework", "theory"],
  },
  {
    id: "lib-sinks-faucets",
    disciplineIds: ["economy"],
    title: "Sinks & Faucets",
    summary: "How currency enters and leaves an economy, and why balancing the two keeps it healthy.",
    body: `## The model
An in-game economy is plumbing. **Faucets** add resources (quest rewards, drops); **sinks** remove them (crafting costs, repairs, fees). When faucets outpace sinks, value inflates and rewards feel meaningless; when sinks dominate, players feel starved.

## Designing balance
- Pair every major faucet with a meaningful sink.
- Use **self-correcting** sinks (costs that scale with wealth).
- Watch for **unintended faucets** (exploits, infinite farming).

## Health signals
- Are veteran players hoarding infinitely? (sink too weak)
- Do new players feel constantly broke? (faucet too weak)

## Key takeaways
- Track the *flow*, not just the totals.
- Inflation is usually a sink problem, not a faucet problem.`,
    sources: [
      { title: "Game Balance", author: "Ian Schreiber & Brenda Romero" },
    ],
    tags: ["economy", "balance"],
  },
  {
    id: "lib-pacing",
    disciplineIds: ["level-design"],
    title: "Pacing & the Tension Curve",
    summary: "Shaping the rhythm of intensity so peaks land and players don't burn out.",
    body: `## Rhythm, not a flat line
Sustained intensity numbs players; sustained calm bores them. Good pacing alternates **tension and release** in a deliberate curve, usually rising toward a climactic peak before resolving.

## Tools
- **Beat charts** map intended emotion across a level's minutes.
- **Quiet beats** before a peak make the peak hit harder (contrast).
- Vary **threat density, space, and lighting** to control felt intensity.

## Common mistakes
- Back-to-back peaks (fatigue).
- No breather before the finale.

## Key takeaways
- Contrast creates impact.
- Plan the emotional curve before placing encounters.`,
    sources: [
      { title: "Level Up! The Guide to Great Video Game Design", author: "Scott Rogers" },
    ],
    tags: ["pacing", "level"],
  },
  {
    id: "lib-env-storytelling",
    disciplineIds: ["narrative", "level-design"],
    title: "Environmental Storytelling",
    summary: "Telling story through space, objects, and staging rather than text or cutscenes.",
    body: `## The idea
Players infer narrative from **arranged evidence**: a toppled chair, scorch marks, a barricaded door. The space becomes a frozen moment the player reconstructs.

## Techniques
- **Staging** — compose scenes like a snapshot of an event mid-action.
- **Juxtaposition** — place objects whose contrast implies a story.
- **Leave gaps** — players enjoy filling in cause and consequence.

## Why it works
It respects player intelligence and pacing — story is absorbed *while playing*, not by stopping play.

## Key takeaways
- Show evidence, not exposition.
- Ambiguity invites the player to co-author meaning.`,
    sources: [
      { title: "Environmental Storytelling (GDC talks)", author: "Don Carson; Harvey Smith & Matthias Worch" },
    ],
    tags: ["narrative", "environmental"],
  },
  {
    id: "lib-game-feel",
    disciplineIds: ["game-feel"],
    title: "Game Feel & Juice",
    summary: "The moment-to-moment tactile sensation of control, and the polish that amplifies it.",
    body: `## Game feel
**Game feel** is the real-time, tactile sense of controlling something in a virtual space — responsiveness, weight, and the satisfaction of input mapping to action.

## Juice
**Juice** is the layer of feedback that makes actions feel impactful: screen shake, particles, squash-and-stretch, sound, hit-stop, and timing. It rewards the player generously for ordinary inputs.

## Principles
- Keep input latency low; responsiveness is foundational.
- Layer feedback across **visual, audio, and haptic** channels.
- Respect a budget — too much juice becomes noise.

## Key takeaways
- Feel is designed, not accidental.
- A small action can carry a big reaction.`,
    sources: [
      { title: "Game Feel: A Game Designer's Guide to Virtual Sensation", author: "Steve Swink" },
    ],
    tags: ["juice", "feel"],
  },
  {
    id: "lib-teaching",
    disciplineIds: ["onboarding"],
    title: "Teaching Without Tutorials",
    summary: "Helping players learn mechanics through play, safe failure, and careful sequencing.",
    body: `## Learn by doing
Players retain what they *discover* far better than what they're *told*. Well-designed games teach through structured play rather than text walls.

## The sequence
1. **Introduce** the mechanic in a safe space (failure is harmless).
2. **Force** its use to progress.
3. **Complicate** it gradually.
4. **Confirm** mastery before the real challenge.

## Supports
- Strong **affordances** (the design suggests the action).
- Just-in-time prompts only when discovery fails.

## Key takeaways
- Safe failure is the best teacher.
- Sequence difficulty deliberately; don't dump.`,
    sources: [
      { title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" },
    ],
    tags: ["onboarding", "teaching"],
  },
  {
    id: "lib-flow",
    disciplineIds: ["progression"],
    title: "Flow & Difficulty",
    summary: "Matching challenge to skill so players stay in an engaged, focused state.",
    body: `## The flow channel
Players engage best when **challenge** roughly tracks their **skill**. Too hard breeds anxiety; too easy breeds boredom. The target is a moving "flow channel" between the two as skill grows.

## Keeping players in flow
- Ramp difficulty alongside expected mastery.
- Offer **clear goals** and **immediate feedback**.
- Provide difficulty options or dynamic adjustment for varied players.

## Caution
Flow isn't the only goal — deliberate spikes (boss fights) and lulls create rhythm and meaning.

## Key takeaways
- Difficulty should rise with the player.
- Clear goals + feedback sustain engagement.`,
    sources: [
      { title: "Flow: The Psychology of Optimal Experience", author: "Mihaly Csikszentmihalyi" },
      { title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" },
    ],
    tags: ["difficulty", "flow"],
  },
  {
    id: "lib-readability",
    disciplineIds: ["ux", "signposting"],
    title: "Readability & Signposting",
    summary: "Guiding attention and communicating state so players always understand what matters.",
    body: `## Communicate at a glance
Players make fast decisions; the design must make the important things **instantly readable**. Use silhouette, color, contrast, motion, and composition to direct attention.

## Signposting
**Signposting** guides players where to go and what's interactable — through lighting, leading lines, color accents, and consistent visual grammar.

## Rules of thumb
- One clear visual language for "interactive", "danger", "goal".
- Don't rely on color alone (accessibility).
- Reduce noise so signal stands out.

## Key takeaways
- Attention is a budget — spend it on what matters.
- Consistency is what makes signposting trustworthy.`,
    sources: [
      { title: "The Design of Everyday Things", author: "Don Norman" },
      { title: "Designing Games", author: "Tynan Sylvester" },
    ],
    tags: ["readability", "ux"],
  },
  {
    id: "lib-encounter",
    disciplineIds: ["combat"],
    title: "Encounter Design & Telegraphing",
    summary: "Building fair, readable combat encounters where threats are understandable and counterable.",
    body: `## Fair challenge
A good encounter is **hard but fair**: the player can read incoming threats and has a counter. Unfairness usually comes from unreadable or untelegraphed danger.

## Telegraphing
**Telegraphs** are the tells before an attack — wind-ups, audio cues, visual flashes — giving the player a window to react. Their length tunes difficulty.

## Composing encounters
- Mix enemy **roles** (pressure, ranged, disruptor) for interesting decisions.
- Control **space and density** to set intensity.
- Teach an enemy in isolation before combining it.

## Key takeaways
- Readable threats make difficulty feel fair.
- Telegraph length is a core difficulty dial.`,
    sources: [
      { title: "Game Design GDC talks on encounter design", author: "various" },
    ],
    tags: ["combat", "encounter"],
  },
  {
    id: "lib-accessibility",
    disciplineIds: ["accessibility"],
    title: "Accessibility Foundations",
    summary: "Designing so more players can play — across vision, hearing, motor, and cognitive needs.",
    body: `## Why it matters
Accessibility expands your audience and almost always improves usability for *everyone*. Many features (remappable controls, subtitles, scalable UI) are low-cost when planned early.

## Common considerations
- **Visual** — colorblind-safe signals (never color alone), scalable text, high contrast.
- **Auditory** — subtitles, visual cues for important sounds.
- **Motor** — remappable inputs, hold-vs-toggle, adjustable timing.
- **Cognitive** — clear language, optional hints, adjustable difficulty.

## Approach
Build accessibility in from the start rather than bolting it on; treat options as first-class design.

## Key takeaways
- Don't encode meaning in a single channel.
- Early planning makes accessibility cheap.`,
    sources: [
      { title: "Game Accessibility Guidelines", url: "https://gameaccessibilityguidelines.com" },
    ],
    tags: ["accessibility", "inclusive"],
  },

  // ===================== From the study shelf (distilled, cited) =====================

  {
    id: "lib-elemental-tetrad",
    disciplineIds: ["systems"],
    title: "The Elemental Tetrad & Lenses",
    summary: "Schell's model: every game is built from Mechanics, Story, Aesthetics, and Technology — held in balance and inspected through 'lenses'.",
    body: `## The four elements
Jesse Schell frames every game as four interlocking elements:
- **Mechanics** — the rules, goals, and procedures; what makes it a game rather than a story or a toy.
- **Story** — the sequence of events, authored or emergent, that unfolds.
- **Aesthetics** — how the game looks, sounds, and feels; the most direct line to the player's experience.
- **Technology** — the materials and platform that make the rest possible.

None is more important than the others, and the player sees aesthetics first while mechanics are least visible. The designer's job is to keep all four **reinforcing the same experience**.

## Lenses
The book's central tool is the **lens**: a short set of questions you hold up to your design to see it from one angle (the Lens of Fun, of the Player, of Surprise, of the Elemental Tetrad, and ~100 more). No single lens is the truth; each reveals a different facet.

## How to use it
- Name the **experience** you want first, then check each element serves it.
- When something feels off, rotate through lenses to locate which element is fighting the others.
- Watch for an element pulling against the rest (gorgeous aesthetics on a hollow mechanic).

## Key takeaways
- Balance the tetrad; the player feels the whole, not the parts.
- Lenses are deliberate perspective shifts, not a checklist to "pass".`,
    sources: [{ title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" }],
    tags: ["framework", "theory", "fundamentals"],
  },

  {
    id: "lib-eight-kinds-of-fun",
    disciplineIds: ["systems"],
    title: "The Pleasures of Play",
    summary: "'Fun' isn't one thing. Naming the specific pleasures a game targets makes design trade-offs decidable.",
    body: `## Fun is plural
Designers reach for the word *fun*, but it bundles many distinct pleasures. Schell (drawing on LeBlanc's "8 kinds of fun") lists pleasures such as **sensation, fantasy, narrative, challenge, fellowship, discovery, expression,** and **submission** (the comfort of a familiar routine). Most games chase a few of these, not all.

## Why it matters
If you cannot say *which* pleasures your game delivers, you cannot tell whether a change helps or hurts. Naming the target pleasures turns vague "is this fun?" debates into answerable questions ("does this serve discovery, which we said was core?").

## In practice
- List your game's **two or three primary pleasures**, ranked.
- For every feature, ask which listed pleasure it serves; cut or rework features that serve none.
- Look for **anticipation, completion, and surprise** as reliable sources of delight.

## Key takeaways
- "Fun" is a category, not a goal — specify the pleasure.
- A focused game nails a few pleasures rather than diluting many.`,
    sources: [
      { title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" },
      { title: "MDA: A Formal Approach to Game Design", author: "Hunicke, LeBlanc & Zubek" },
    ],
    tags: ["aesthetics", "theory", "fun"],
  },

  {
    id: "lib-interest-curves",
    disciplineIds: ["level-design", "progression"],
    title: "Interest Curves",
    summary: "A guest's engagement over time can be drawn as a curve — and good experiences share a recognizable shape.",
    body: `## The shape of engagement
Schell borrows from entertainment design: plot a guest's **interest** on the Y axis against **time** on the X axis. A satisfying experience tends to open with a **hook** (a spike that earns attention), then a series of rising-and-falling beats that trend **upward** to a **climax**, followed by a short release.

## Fractal structure
The curve is **fractal** — the whole game has one, each level has one, each encounter has one. A great moment-to-moment curve can still sit inside a poorly shaped session, so design the curve at every scale.

## Designing with it
- Start with a real **hook**, not a slow warm-up.
- Alternate tension and relief; you cannot stay at peak intensity.
- Place the **highest point near the end**, then resolve.
- Sketch the intended curve, playtest, and compare the felt curve to the drawn one.

## Key takeaways
- Engagement has a shape you can deliberately author.
- Contrast (rises and dips) is what makes the peaks land.`,
    sources: [{ title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" }],
    tags: ["pacing", "engagement", "structure"],
  },

  {
    id: "lib-fun-is-learning",
    disciplineIds: ["progression", "onboarding"],
    title: "Fun Is Learning",
    summary: "Koster's thesis: fun is the feeling of your brain mastering a pattern. When there's nothing left to learn, the fun drains out.",
    body: `## The core idea
Raph Koster argues that **fun is the act of mastering a problem mentally** — the brain enjoys absorbing new patterns. A game is, in this view, a teacher in disguise: it presents a pattern, and the pleasure is the click of grokking it.

## The two failure modes
- **Boredom** — the pattern is fully learned; there's nothing new to chew on, so the game goes "flat". Players quit games they have *solved*.
- **Noise** — the pattern is too opaque to read; the player can't find the regularity, so it feels like chaos, not challenge.

Good design lives between these: a steady supply of **fresh, graspable patterns**.

## Implications
- Keep introducing new wrinkles before mastery flattens the curve.
- If players bounce off, ask whether the pattern is unlearnable (noise) or already trivial (boredom).
- Aesthetics, story, and social layers keep a game compelling *after* its patterns are mastered.

## Key takeaways
- A game stops being fun the moment it's fully understood.
- Pace the delivery of new patterns; that pacing *is* the fun.`,
    sources: [{ title: "A Theory of Fun for Game Design", author: "Raph Koster" }],
    tags: ["learning", "theory", "engagement"],
  },

  {
    id: "lib-emergence-progression",
    disciplineIds: ["systems", "level-design"],
    title: "Emergence vs. Progression",
    summary: "Adams & Dormans' two structural families: games that emerge from interacting rules, and games that progress through authored content.",
    body: `## Two ways to build a game
- **Games of emergence** — a small set of rules that interact to yield a huge **possibility space** (chess, the Sims, strategy games). Replayable, systems-driven, hard to fully author or predict.
- **Games of progression** — designer-authored sequences the player moves through (story-driven levels, scripted set-pieces). Controllable and cinematic, but largely consumed once.

## The trade-off
Emergence gives **depth and replayability** for relatively little content, at the cost of control. Progression gives **authored, curated experiences** at the cost of high content burn and low replay. Most modern games **blend** both — emergent core systems carried along a progression spine of levels and unlocks.

## Designing the mix
- Decide which spine your game leans on, then use the other to support it.
- For emergence, invest in **rules that interact**; for progression, invest in **content pipelines** and pacing.
- Beware progression masquerading as depth (a long corridor of one-time events).

## Key takeaways
- Depth can come from interacting rules *or* authored content — know which you're buying.
- The richest games braid the two together.`,
    sources: [{ title: "Game Mechanics: Advanced Game Design", author: "Ernest Adams & Joris Dormans" }],
    tags: ["systems", "emergence", "structure"],
  },

  {
    id: "lib-internal-economy",
    disciplineIds: ["economy", "systems"],
    title: "The Internal Economy",
    summary: "Most game systems can be modeled as resources flowing through sources, drains, converters, and traders — the 'machinations' view.",
    body: `## Games as flows of resources
Adams & Dormans model a game's systems as an **internal economy**: abstract **resources** (health, gold, ammo, momentum, even map control) that move through a few building blocks:
- **Sources** — produce resources (spawners, income, regen).
- **Drains** — consume them (costs, decay, damage).
- **Converters** — turn one resource into another (smelting ore into bars).
- **Traders** — exchange resources between actors (shops, treaties).

## Why model it
Drawing the economy makes **balance and pacing legible**. You can see where resources pool, where they starve, and which loops dominate — before you ever build the content. Dormans' **Machinations** diagrams let you simulate these flows.

## In practice
- Inventory every resource, even intangible ones (tempo, aggro, information).
- Map each source to a drain; unmatched sources cause inflation, unmatched drains cause starvation.
- Find the **dominant loop** and check it isn't trivially exploitable.

## Key takeaways
- Almost any mechanic is a resource flow in disguise.
- Diagramming the economy surfaces balance problems on paper, cheaply.`,
    sources: [{ title: "Game Mechanics: Advanced Game Design", author: "Ernest Adams & Joris Dormans" }],
    tags: ["economy", "systems", "machinations"],
  },

  {
    id: "lib-feedback-loops",
    disciplineIds: ["systems", "economy", "multiplayer"],
    title: "Feedback Loops",
    summary: "Positive loops amplify a lead and end games fast; negative loops stabilize and keep them close. Knowing which you have is half of balancing.",
    body: `## Two kinds of loop
- **Positive feedback** — success makes further success easier (a lead snowballs). It **accelerates** outcomes, rewards early advantage, and can create runaway winners or "rich get richer" spirals.
- **Negative feedback** — success makes things harder, or failure gets help (rubber-banding, blue shells, comeback bonuses). It **stabilizes**, keeps players close, and extends play.

## Why it matters
The loops in your systems decide a game's **length, tension, and fairness**. Too much positive feedback and matches are decided early and feel hopeless to the loser. Too much negative feedback and skill stops mattering — leads feel meaningless.

## Tuning them
- Use **positive** loops to bring games to a decisive close and reward mastery.
- Use **negative** loops to keep contenders in reach and protect pacing.
- Make negative feedback **scale with the gap** so it fades as it works, and never fully erases earned advantage.

## Key takeaways
- Identify every feedback loop and label its sign.
- Balance is largely the art of mixing amplifying and stabilizing loops.`,
    sources: [
      { title: "Game Mechanics: Advanced Game Design", author: "Ernest Adams & Joris Dormans" },
      { title: "Game Balance", author: "Ian Schreiber & Brenda Romero" },
    ],
    tags: ["systems", "balance", "feedback"],
  },

  {
    id: "lib-balance-vocabulary",
    disciplineIds: ["economy", "systems"],
    title: "The Vocabulary of Balance",
    summary: "Schreiber & Romero's toolkit for reasoning about fairness: possibility space, determinism, symmetry, solvability, and the metagame.",
    body: `## Words that make balance discussable
*Game Balance* gives precise terms for what is otherwise hand-waved:
- **Possibility space** — the set of all reachable game states. Design is shaping this space.
- **Determinism** — how much outcomes follow from inputs vs. chance; pure determinism is solvable, pure chance is luck.
- **Symmetry / asymmetry** — whether sides start equal. Symmetry is "fair" but can be dull; asymmetry is rich but hard to balance.
- **Solvability** — whether an optimal strategy exists and is findable. A **solved** game (or a dominant strategy) is dead on arrival.
- **The metagame** — the layer of strategy *around* the game (deck choices, tier lists, counter-picks) that keeps a balanced game alive.

## Kinds of balance
Balance isn't one thing: **fairness** between players, **difficulty** against the game, **progression** over time, and **balance among options** (no single dominant choice). These can conflict — tuning one may unbalance another.

## In practice
- Hunt for **dominant strategies**; their existence means the possibility space collapsed.
- Add meaningful **intransitivity** or hidden information to resist solving.
- Let the **metagame** carry variety you can't bake into the base rules.

## Key takeaways
- Precise vocabulary turns "it feels unfair" into a fixable diagnosis.
- A game that can be solved will be — design against it.`,
    sources: [{ title: "Game Balance", author: "Ian Schreiber & Brenda Romero" }],
    tags: ["balance", "theory", "vocabulary"],
  },

  {
    id: "lib-transitive-intransitive",
    disciplineIds: ["economy", "combat"],
    title: "Transitive & Intransitive Mechanics",
    summary: "Power that ranks in a line (transitive) is balanced with cost curves; power that loops like rock-paper-scissors (intransitive) is balanced by matchups.",
    body: `## Two ways things relate
- **Transitive** — options sit on a single scale: if A beats B and B beats C, then A beats C. A stronger sword, a higher tier. Balance here means **cost**: better things must cost more, on a deliberate **cost curve**.
- **Intransitive** — options form a loop with no single best: rock beats scissors beats paper beats rock. Balance here comes from **matchups and frequency**, not price.

## Cost curves
For transitive systems, plot **power vs. cost**. A straight line means choices are interchangeable; a curve that bends creates interesting decisions (cheap-but-efficient vs. expensive-but-powerful). Outliers off the curve become must-picks or traps.

## Designing intransitivity
Rock-paper-scissors relationships create **counterplay** and keep no option dominant — but only if each choice is viable and players have information to respond. Hidden simultaneous choice (as in RPS itself) preserves tension.

## In practice
- Decide per system whether power is a line or a loop.
- For lines, set and audit a cost curve; for loops, verify every option has a role.
- Mix both: a transitive power ladder *within* an intransitive class triangle.

## Key takeaways
- Transitive balance is priced; intransitive balance is matched.
- Most deep combat blends a power curve with counter-relationships.`,
    sources: [{ title: "Game Balance", author: "Ian Schreiber & Brenda Romero" }],
    tags: ["balance", "combat", "cost-curve"],
  },

  {
    id: "lib-game-feel-metrics",
    disciplineIds: ["game-feel", "controls"],
    title: "Measuring Game Feel",
    summary: "Swink defines feel as real-time control of a virtual body in a simulated space, made to feel good — and gives metrics to tune it.",
    body: `## A definition you can act on
Steve Swink defines **game feel** as the tactile, kinesthetic sensation of controlling a virtual object. He breaks it into three building blocks: **real-time control**, an **avatar in a simulated space**, and **polish** — the layers of effect that make interaction satisfying.

## From intuition to metrics
The book's contribution is making feel **measurable** so "floaty" vs. "tight" stop being vibes. Swink groups the tunable pieces into metrics:
- **Input** — the device and how its signal maps to action.
- **Response** — how the avatar reacts over time (acceleration, friction, turn speed).
- **Context** — the space and objects that give motion meaning (gaps, walls, targets).
- **Polish** — particles, screen shake, sound, and camera that amplify the moment.
- **Metaphor** — what the avatar represents, setting expectations for how it should move.
- **Rules** — the higher-level constraints and goals around the motion.

## Tuning loop
Sensitivity lives in tiny numbers: a few frames of input delay or a small change in acceleration can flip a game from sluggish to crisp. Tune **one metric at a time** and feel the difference.

## Key takeaways
- Feel = control + simulated body + polish, and each is tunable.
- Most of the player's time is spent *inside* feel — budget effort accordingly.`,
    sources: [{ title: "Game Feel: A Game Designer's Guide to Virtual Sensation", author: "Steve Swink" }],
    tags: ["game-feel", "controls", "tuning"],
  },

  {
    id: "lib-meaningful-play",
    disciplineIds: ["systems", "ux"],
    title: "Meaningful Play & the Magic Circle",
    summary: "Salen & Zimmerman: play becomes meaningful when actions have discernible, integrated consequences — inside the special space of the 'magic circle'.",
    body: `## Meaningful play
Salen & Zimmerman define the goal of design as **meaningful play**, which arises when the relationship between a player's **action** and the system's **outcome** is both:
- **Discernible** — the player can perceive the result of what they did, and
- **Integrated** — that result matters to the larger game, not just the moment.

When feedback is unclear or actions don't ripple forward, play feels arbitrary. Meaning is engineered through legible, consequential choices.

## The magic circle
Entering a game means stepping into the **magic circle** — a bounded space where ordinary actions take on special, agreed-upon meaning (moving a pawn "means" something here that it wouldn't on a random table). The circle is fragile: it holds only while players accept the rules.

## In practice
- For every action, ask: can the player **see** the outcome, and does it **carry forward**?
- Tighten feedback first when a system feels meaningless.
- Protect the magic circle — exploits, unclear rules, and broken promises pop it.

## Key takeaways
- Meaning = discernible + integrated consequences.
- Games create a temporary world where actions gain new significance; guard it.`,
    sources: [{ title: "Rules of Play: Game Design Fundamentals", author: "Katie Salen & Eric Zimmerman" }],
    tags: ["theory", "meaning", "fundamentals"],
  },

  {
    id: "lib-intrinsic-motivation",
    disciplineIds: ["metagame", "progression"],
    title: "Intrinsic Motivation & Honest Gamification",
    summary: "Points and badges layered onto a hollow activity wear off; lasting engagement comes from autonomy, mastery, and purpose.",
    body: `## Extrinsic vs. intrinsic
**Extrinsic** rewards (points, badges, leaderboards — the "PBL" of shallow gamification) can spark short-term behavior but fade and can even **crowd out** the underlying motivation. **Intrinsic** motivation — doing it because the activity itself satisfies — is durable.

McGonigal frames great games as deliverers of intrinsic reward: **satisfying work, the hope of success, social connection, and meaning**. Burke cautions that bolting reward mechanics onto a chore rarely makes it a game.

## Autonomy, mastery, purpose
Lasting engagement leans on three motivators:
- **Autonomy** — meaningful choices and self-direction.
- **Mastery** — visible, earned competence (see *Fun Is Learning* and *Flow & Difficulty*).
- **Purpose** — a goal that feels worth pursuing, often social or narrative.

## In practice
- Make the **core activity** rewarding before adding any meta-rewards.
- Use extrinsic systems to *signpost* progress, not to *be* the reason to play.
- Offer real choices and surface mastery; don't manufacture FOMO.

## Key takeaways
- PBL on a boring task is lipstick; fix the task.
- Build for autonomy, mastery, and purpose — they don't wear off.`,
    sources: [
      { title: "Reality Is Broken", author: "Jane McGonigal" },
      { title: "Gamify", author: "Brian Burke" },
    ],
    tags: ["motivation", "engagement", "live-ops"],
  },

  {
    id: "lib-level-design-process",
    disciplineIds: ["level-design"],
    title: "The Level Design Process",
    summary: "Levels are built in passes — intent, layout, whitebox, test, polish — and judged by flow, gating, and how they guide the eye.",
    body: `## Levels are iterated, not drawn once
A practical level pipeline moves in deliberate passes:
1. **Intent** — what should this space teach, test, or make the player feel?
2. **Layout / bubble diagram** — abstract flow of spaces, beats, and connections before any geometry.
3. **Whitebox (greybox)** — build it in rough blocks and **play it immediately**; prove the space works before art.
4. **Test & iterate** — watch players, find where they get lost or bored, reshape.
5. **Polish & set-dressing** — only once the space plays well does it get its final art and detail.

## Core craft
- **Pacing & beats** — alternate tension and rest along the path (see *Pacing & the Tension Curve*).
- **Gating & flow** — control where players can go and when; use locks, sightlines, and one-way drops to shape the route.
- **Guiding the eye** — lead players with light, contrast, framing, and landmarks instead of arrows (see *Readability & Signposting*).
- **Composition** — every space reads as a picture from the player's likely vantage.

## Key takeaways
- Whitebox and playtest before you spend a minute on art.
- A level is a guided experience — author the path, the pace, and the gaze.`,
    sources: [
      { title: "A Practical Guide to Level Design", author: "Benjamin Bauer" },
      { title: "Level Up! The Guide to Great Video Game Design", author: "Scott Rogers" },
    ],
    tags: ["level-design", "process", "whitebox"],
  },

  {
    id: "lib-formal-elements",
    disciplineIds: ["systems"],
    title: "The Formal Elements of Games",
    summary: "Fullerton's playcentric checklist of the structural parts every game has — a vocabulary for taking a design apart.",
    body: `## The structural anatomy
Tracy Fullerton's *Game Design Workshop* identifies the **formal elements** present in every game. Naming them lets you analyze and tune a design piece by piece:
- **Players** — how many, and their pattern of interaction (solo, head-to-head, cooperative, multilateral).
- **Objectives** — what players are trying to achieve (capture, chase, build, solve, outlast).
- **Procedures** — the actions players take and in what order.
- **Rules** — what is allowed, forbidden, and what effects follow.
- **Resources** — assets of value the rules make scarce.
- **Conflict** — the obstacles between players and their objectives (opponents, dilemmas, the system itself).
- **Boundaries** — where the game ends and the real world begins (the magic circle).
- **Outcome** — how it resolves, and whether that resolution is measurable and uncertain.

## Why use it
When a design feels off, walk the list: a flat game often has a weak **objective**, too few **resources** to fight over, or no real **conflict**. The elements also expose your **player-interaction pattern**, which quietly shapes everything else.

## Key takeaways
- Every game can be disassembled into these parts.
- Tuning is easier when you know which element is underpowered.`,
    sources: [{ title: "Game Design Workshop: A Playcentric Approach", author: "Tracy Fullerton" }],
    tags: ["framework", "fundamentals", "systems"],
  },

  {
    id: "lib-prototyping-playtesting",
    disciplineIds: ["playtesting", "scoping"],
    title: "Prototyping & Playtesting",
    summary: "Find the fun fast with cheap prototypes, then improve it with structured playtests that watch behavior, not opinions.",
    body: `## Prototype to find the fun
The playcentric method front-loads **prototyping**: build the cheapest thing that can answer your biggest question. Paper, dice, and cardboard test rules and economies before a line of code. The point isn't to look like the game — it's to **feel** the core decision and prove it's worth building.

## Playtest to improve it
A **playtest** is a controlled observation, not a demo:
- **Recruit the right testers** — your target players, not just friends (friends are kind; strangers are honest). Rotate fresh testers in; veterans stop seeing first-time problems.
- **Watch what they do, not (only) what they say.** Where do they hesitate, get lost, or quit? Behavior beats stated preference.
- **Don't help.** Sit on your hands; the moment you explain, you've lost the data.
- **Ask open questions** afterward, and look for the *problem* behind a tester's *proposed solution*.

## The loop
Prototype → test → analyze → iterate, in tight cycles. Each loop should answer one question and raise the next.

## Key takeaways
- The cheapest prototype that answers the question wins.
- Players are unreliable narrators; trust observed behavior.`,
    sources: [
      { title: "Game Design Workshop: A Playcentric Approach", author: "Tracy Fullerton" },
      { title: "Challenges for Game Designers", author: "Brenda Brathwaite & Ian Schreiber" },
    ],
    tags: ["prototyping", "playtesting", "iteration"],
  },

  {
    id: "lib-player-types",
    disciplineIds: ["multiplayer", "metagame"],
    title: "Player Types & Player-Centric Design",
    summary: "Design for a defined audience. Bartle's archetypes (and their kin) remind us players want very different things from the same game.",
    body: `## Design for someone specific
Ernest Adams' **player-centric** stance: picture a concrete target player and make decisions on their behalf, rather than for a vague "everyone." Knowing who you're for makes trade-offs decidable.

## Bartle's archetypes
Richard Bartle's classic split (from MUD research) maps players on two axes — acting vs. interacting, world vs. players:
- **Achievers** — chase goals, points, completion, mastery of the game.
- **Explorers** — probe the systems and the world for its secrets and edges.
- **Socializers** — play for the people; the game is a backdrop for relationships.
- **Killers** — seek to act *on* other players: competition, domination, disruption.

Most players are a blend, but a population skews, and the types **shape each other** — killers need targets, socializers need a scene, achievers need an audience.

## In practice
- State which types your game serves *primarily*; you cannot fully serve all four.
- Provide goals (achievers), depth and secrets (explorers), social tools (socializers), and fair competition (killers) in the proportions your game wants.
- Watch the **ecology**: too many killers drive off socializers and the whole scene collapses.

## Key takeaways
- "Players" is not one audience — name yours.
- A multiplayer community is an ecosystem of types that must stay in balance.`,
    sources: [
      { title: "Fundamentals of Game Design", author: "Ernest Adams" },
      { title: "Hearts, Clubs, Diamonds, Spades: Players Who Suit MUDs", author: "Richard Bartle" },
    ],
    tags: ["player-types", "audience", "social"],
  },

  {
    id: "lib-types-of-challenge",
    disciplineIds: ["puzzle", "systems"],
    title: "Types of Challenge",
    summary: "Adams catalogs the pure challenges games pose — naming them helps you vary difficulty and target the skills you intend.",
    body: `## Challenge is the engine
Most gameplay is a stack of **challenges**. Adams distinguishes *intrinsic* skills (ones the game is about) from *extrinsic* ones (incidental), and catalogs recurring challenge types so you can choose and combine them deliberately:
- **Logic & deduction** — reason from rules to a solution.
- **Pattern recognition** — spot and exploit regularities.
- **Spatial reasoning** — navigate, rotate, fit, and route through space.
- **Time pressure & reflex** — act correctly *and* fast.
- **Memory** — recall information the game withholds.
- **Knowledge & exploration** — learn the world to progress.
- **Economic / optimization** — allocate scarce resources well.

## Designing with the menu
- **Layer** challenge types so different players find a foothold and experts juggle several at once.
- **Vary difficulty along one axis at a time** — raising time pressure and complexity together spikes unfairly.
- Match the challenge to the **fantasy**: a detective game leans on deduction, a platformer on spatial + reflex.

## Key takeaways
- Name the skill each challenge actually tests.
- Difficulty is multi-dimensional; tune one dimension at a time.`,
    sources: [{ title: "Fundamentals of Game Design", author: "Ernest Adams" }],
    tags: ["challenge", "difficulty", "puzzle"],
  },

  {
    id: "lib-puzzle-design",
    disciplineIds: ["puzzle"],
    title: "Puzzle Design Principles",
    summary: "Great puzzles hinge on one hidden insight, clue it fairly, and reward thinking over guessing.",
    body: `## The anatomy of a good puzzle
A satisfying puzzle is built around a single **insight** — a realization that, once had, makes the solution obvious. The craft is in the journey to that click:
- **One core idea per puzzle.** Compounding two unsolved insights creates frustration, not depth.
- **Clue it fairly.** Everything needed to solve must be present and perceivable; the "aha" should feel inevitable in hindsight.
- **Block the obvious-but-wrong paths** deliberately, so the player is pushed to question an assumption.
- **Reward reasoning, not brute force.** If trial-and-error is faster than thinking, it isn't a puzzle.

## The teaching arc
Across a set, the classic structure is **teach → complicate → twist → master**: introduce a mechanic safely, add wrinkles, reveal a non-obvious property (the twist that reframes the rule), then demand mastery. Each step is a *new idea*, not just a new layout.

## In practice
- After designing, ask: *what is the one thing the player must realize?* If you can't answer, the puzzle is muddy.
- Test for unintended solutions and for the "stuck wall" where your clue isn't landing.

## Key takeaways
- A puzzle is a single insight, fairly hidden.
- Author the moment of realization, not just the obstacle.`,
    sources: [
      { title: "Challenges for Game Designers", author: "Brenda Brathwaite & Ian Schreiber" },
      { title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" },
    ],
    tags: ["puzzle", "insight", "teaching"],
  },

  {
    id: "lib-believable-ai",
    disciplineIds: ["ai-behavior"],
    title: "Believable Enemy AI",
    summary: "Game AI sells the illusion of intelligence through readable behavior and telegraphing — it's theatre, not a Turing test.",
    body: `## AI as performance
Game AI rarely needs to be *smart*; it needs to be **believable and fun to play against**. The goal is the *illusion* of intent, achieved with simple, legible behavior:
- **Finite state machines** — a few clear states (patrol, alert, attack, flee) with visible transitions. Easy to author, easy for players to read.
- **Behavior trees** — scalable decision structures for richer characters without spaghetti logic.
- **Utility / influence** — score options by context to pick "good enough" actions.

## Make it readable
Players must be able to **predict and counter** the AI, so behavior has to be telegraphed:
- **Tells & wind-ups** announce attacks (see *Encounter Design & Telegraphing*).
- **Barks and animation** externalize internal state ("Did you hear that?" signals the alert transition).
- **Be reliably stupid in legible ways.** Exploitable, understandable enemies are more fun than perfect ones.

## Directors
Above individual enemies, a **director** can pace tension across a whole encounter — easing off when the player struggles, pressing when they coast — while staying invisible.

## Key takeaways
- Sell intent, don't simulate a mind.
- If the player can't read it, it isn't good AI — it's noise.`,
    sources: [
      { title: "Game Programming Patterns", author: "Robert Nystrom" },
      { title: "Fundamentals of Game Design", author: "Ernest Adams" },
    ],
    tags: ["ai", "enemy-design", "readability"],
  },

  {
    id: "lib-player-psychology",
    disciplineIds: ["ux", "onboarding"],
    title: "How Players Actually Think",
    summary: "Players build mental models from feedback and rarely read instructions. Design for the brain you have, not the one you wish you had.",
    body: `## Players are doers, not readers
Hazelden's *Secret Science of Games* and usability research agree: players **skip text**, act on assumptions, and learn by poking. They build a **mental model** of how your game works from the feedback you give — and if your feedback is misleading, their model will be wrong and they'll blame the game.

## Principles that follow
- **Feedback teaches.** Every action needs a clear, immediate result; ambiguous feedback breeds superstition.
- **Affordances over instructions.** Make the right action *look* possible (a ledge that's clearly grabbable) rather than telling the player about it.
- **Respect attention and memory.** Players forget; surface information when it's needed, not all at once up front.
- **Failure should inform.** A death the player understands is a lesson; one they don't is unfair.
- **The curse of knowledge.** You know how it works, so you can't see what's confusing — only fresh testers can.

## In practice
- Trace each new mechanic: what feedback builds the *correct* mental model?
- Replace tutorial text with situations that teach by doing and safe failure.

## Key takeaways
- Players learn from feedback, not from your manual.
- Design the experience around real cognition — limited attention, model-building, skipped text.`,
    sources: [
      { title: "The Secret Science of Games", author: "John Hazelden" },
      { title: "A Theory of Fun for Game Design", author: "Raph Koster" },
    ],
    tags: ["psychology", "ux", "mental-model"],
  },

  {
    id: "lib-story-structure",
    disciplineIds: ["narrative-delivery", "narrative"],
    title: "Story Structure & Delivery",
    summary: "Games tell stories through space and play, not just cutscenes — braid authored beats with player agency, and show rather than tell.",
    body: `## Stories that survive interactivity
Traditional structure (a three-act arc, rising tension, a climax, resolution) still anchors game stories — but games add the player's **agency**, so a screenwriter's straight line must become a branching, environmental, and emergent weave.

## Channels of delivery
Where film has the cut, games can deliver story through many channels — choose the least intrusive that works:
- **Diegetic / environmental** — the world itself carries the tale (ruins, graffiti, aftermath); the player infers (see *Environmental Storytelling*).
- **Systemic** — the mechanics author drama (a resource crisis, an emergent rivalry).
- **Embedded** — authored beats: scripted moments, barks, optional logs.
- **Cutscene** — powerful but costly; it stops play and takes control, so spend it rarely.

## Craft principles
- **Show, don't tell** — let players witness and deduce instead of being narrated to.
- **Pace beats against gameplay** — don't dump story in a wall; thread it through action (see *Story Without Stopping*).
- **Pull, then push** — earn the player's curiosity before delivering exposition.

## Key takeaways
- The strongest game stories are *experienced*, not watched.
- Match each beat to the least disruptive delivery channel.`,
    sources: [
      { title: "O Design como Storytelling", author: "(study notes)" },
      { title: "Level Up! The Guide to Great Video Game Design", author: "Scott Rogers" },
    ],
    tags: ["narrative", "storytelling", "delivery"],
  },

  {
    id: "lib-monetization-models",
    disciplineIds: ["monetization", "metagame"],
    title: "Monetization Models & Ethics",
    summary: "From premium to free-to-play: how games make money, and how to charge without exploiting players.",
    body: `## The main models
- **Premium / buy-to-play** — one upfront price. Simple and honest; revenue is capped by units sold.
- **Free-to-play (F2P)** — free entry, monetized via in-game purchases. Huge reach, but the design *is* the business model.
- **Subscription** — recurring fee for ongoing access/content.
- **Ad-supported** — attention sold to third parties; aligns poorly with deep engagement.
- **Battle pass / season** — a time-bound track of rewards (see the *Season One* challenge).

## What's sold
Sell **cosmetics, convenience, and content** — not raw **power**. Selling power creates **pay-to-win**, which corrodes competition and trust. The healthiest F2P offers value that respects players who pay *and* those who don't.

## Dark patterns to avoid
- **Manufactured FOMO** — artificial scarcity and expiring offers that pressure rather than delight.
- **Obscured costs** — premium currencies that hide real-money price.
- **Pay-to-skip-pain** — designing tedium specifically to sell relief from it.

## A fair test
Would the player feel **respected** if they saw the spreadsheet behind the offer? Sustainable revenue comes from goodwill and a game worth spending on, not from exploiting compulsion.

## Key takeaways
- Sell expression and convenience, not advantage.
- If a system only works by pressuring players, it's a dark pattern — cut it.`,
    sources: [
      { title: "Fundamentals of Game Design", author: "Ernest Adams" },
      { title: "Gamify", author: "Brian Burke" },
    ],
    tags: ["monetization", "ethics", "business"],
  },

  {
    id: "lib-architecture-patterns",
    disciplineIds: ["tech-design"],
    title: "Game Architecture for Designers",
    summary: "A few recurring code patterns shape what's cheap or expensive to change — knowing them helps designers and programmers meet in the middle.",
    body: `## Why a designer cares about architecture
The structure of the code decides which design changes are a five-minute tweak and which are a two-week rewrite. Nystrom's *Game Programming Patterns* names the recurring shapes; a designer who recognizes them can scope ideas realistically and speak the programmer's language.

## Patterns worth knowing
- **Game Loop** — the heartbeat that processes input, updates the world, and renders, ideally decoupled from frame rate so the game runs the same on any hardware.
- **Update Method** — each entity gets a per-frame tick to advance its own behavior; the backbone of "everything does its thing each frame."
- **State (machine)** — model an entity's behavior as named states with explicit transitions (idle → alert → attack). Clean for enemy AI, animations, and any mode-based mechanic.
- **Component** — build entities by *composing* parts (physics, health, AI) instead of deep inheritance trees, so new entity types are mix-and-match.
- **Observer / Event Queue** — let systems react to events without hard-wiring them together, keeping features decoupled.
- **Object Pool** — reuse a fixed set of objects (bullets, particles) instead of constantly allocating, to keep performance smooth.

## Designing with the grain
- Frame mechanics as **states and transitions** — it maps directly onto how they'll be built.
- Prefer **composable** features; "add a component" is cheaper than "subclass everything."
- Ask early what's **expensive to change**; architecture decisions quietly set your iteration speed.

## Key takeaways
- Code structure is a design constraint, not just an implementation detail.
- Think in states, components, and events — the units your game is actually built from.`,
    sources: [{ title: "Game Programming Patterns", author: "Robert Nystrom" }],
    tags: ["architecture", "prototyping", "technical"],
  },

  {
    id: "lib-meaningful-decisions",
    disciplineIds: ["systems"],
    title: "Meaningful Decisions",
    summary: "Sid Meier's 'a game is a series of interesting decisions' — and what actually makes a decision interesting.",
    body: `## The heart of gameplay
A common working definition: a game is a series of **interesting decisions**. If choices are obvious, random, or inconsequential, the game flatlines no matter how pretty it is. Designing gameplay is largely designing *good decisions*.

## What makes a decision interesting
A choice earns the player's attention when it has:
- **A real trade-off** — each option gives something up; there's no free lunch.
- **No dominant option** — if one choice is always best, it isn't a decision (see *The Vocabulary of Balance*).
- **Consequence** — the outcome matters and carries forward (see *Meaningful Play*).
- **Enough information** — the player can reason about it, but not so much that it's solved.
- **Uncertainty** — outcome or opponent response isn't fully known.

## Killers of decisions
- **Obvious choices** — one option clearly wins.
- **Blind choices** — no basis to decide, so it's a coin flip.
- **Empty choices** — nothing rides on it.

## In practice
- For each choice you offer, name the trade-off out loud. If you can't, it isn't interesting yet.
- Hunt and remove dominant options; they silently delete decisions.
- Tune information and uncertainty so the choice is *reasoned*, not *solved* or *guessed*.

## Key takeaways
- Interesting = trade-off + consequence + uncertainty + readable information.
- A game with no real decisions is an activity, not a game.`,
    sources: [
      { title: "Challenges for Game Designers", author: "Brenda Brathwaite & Ian Schreiber" },
      { title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" },
    ],
    tags: ["decisions", "fundamentals", "systems"],
  },

  {
    id: "lib-systems-thinking",
    disciplineIds: ["systems"],
    title: "Systems Thinking for Designers",
    summary: "Sellers' lens: a game is a system of parts, interactions, and loops. Design the relationships, not just the pieces.",
    body: `## Parts, interactions, loops
Systems thinking reframes design from listing features to shaping **relationships**. A system has:
- **Parts** — the elements (units, resources, rules).
- **Interactions** — how parts affect each other.
- **Loops** — chains of interaction that feed back on themselves (see *Feedback Loops*).

The interesting behavior lives in the **interactions and loops**, not the parts alone. Two simple parts with a rich interaction beat two complex parts that ignore each other.

## Stocks and flows
Model state as **stocks** (accumulations: health, gold, trust) and **flows** (the rates that fill or drain them). Most balance and pacing problems are really stock-and-flow problems — a flow set too high or a stock with no drain.

## Emergence and leverage
Designed well, simple rules yield **emergent** behavior the designer never scripted (see *Emergence vs. Progression*). Look for **leverage points** — the few values or rules where a small change swings the whole system — and tune those rather than fighting symptoms.

## In practice
- Diagram parts, interactions, and loops before adding content.
- Identify stocks and the flows that move them; find the unbalanced flow.
- Change one leverage point at a time and observe the whole.

## Key takeaways
- Design the interactions; the parts are just nodes.
- Most balance issues are stock-and-flow issues in disguise.`,
    sources: [{ title: "Advanced Game Design: A Systems Approach", author: "Michael Sellers" }],
    tags: ["systems", "theory", "emergence"],
  },

  {
    id: "lib-core-mechanic",
    disciplineIds: ["systems", "puzzle"],
    title: "The Core Mechanic & Strict Game Design",
    summary: "Burgun's argument for building around a single core mechanic that generates a stream of ambiguous decisions.",
    body: `## One core, deeply explored
Keith Burgun argues for **focus**: a strong game is built around a single **core mechanic** — the central interaction the player repeats — explored to its depth, rather than a pile of loosely related systems. Breadth without a core produces a shallow, unfocused experience.

## Ambiguous decisions
For Burgun, the engine of a (strategy) game is a stream of **ambiguous decisions**: choices where the best option is genuinely unclear, so the player must judge rather than calculate. When decisions become obvious, the game is **solved** and dies. The designer's job is to keep the core decision ambiguous for as long as possible.

## Elegance
Prize **elegance** — maximum depth from minimum rules. Every added rule should pay for its complexity by deepening the core decision; rules that don't are clutter that obscures the game.

## In practice
- State your **one core mechanic** in a sentence; if you can't, the design is unfocused.
- For each rule, ask whether it deepens the core decision or just adds surface.
- Stress-test for a dominant strategy that would resolve the ambiguity and flatten the game.

## Key takeaways
- Depth comes from exploring one core, not adding many.
- Keep the central decision ambiguous; an obvious choice is a dead game.`,
    sources: [{ title: "Game Design Theory: A New Philosophy for Understanding Games", author: "Keith Burgun" }],
    tags: ["core-mechanic", "theory", "elegance"],
  },

  {
    id: "lib-forms-of-play",
    disciplineIds: ["systems", "puzzle"],
    title: "Games, Puzzles, Toys & Contests",
    summary: "Burgun's taxonomy of interactive forms — knowing which one you're making sharpens every other decision.",
    body: `## Four different things
We call everything a "game," but interactive forms differ in kind, and conflating them muddies design. Burgun distinguishes:
- **Toy** — an interactive system with **no goal**; you play *with* it (a ball, a sandbox, a synth). Pure exploration and expression.
- **Puzzle** — a system with a goal and a **solution** to find. Once solved, it's largely spent (see *Puzzle Design Principles*).
- **Contest** — a measure of who performs a known skill best (a race, a high-score run). The optimal play is understood; execution decides it.
- **Game** (in the strict sense) — a goal-driven system of **ambiguous decisions** with no known solution, replayable because mastery is open-ended.

## Why the distinction matters
Each form is judged by different virtues. A toy shouldn't be blamed for lacking a goal; a puzzle isn't failing because it's solvable once. Trouble comes from mixing them carelessly — a "game" that's actually a solved puzzle, or a "toy" players expect to win.

## In practice
- Name which form your core loop really is, and design to *its* strengths.
- Most commercial products **blend** forms (toy-like world + puzzle levels + a strict-game combat core) — but each piece should be intentional.

## Key takeaways
- Toy, puzzle, contest, and game are different things with different virtues.
- Know which you're making before you judge whether it's working.`,
    sources: [{ title: "Game Design Theory: A New Philosophy for Understanding Games", author: "Keith Burgun" }],
    tags: ["taxonomy", "theory", "fundamentals"],
  },

  {
    id: "lib-second-order-design",
    disciplineIds: ["systems", "multiplayer"],
    title: "Second-Order Design",
    summary: "You design the rules, but players design the strategies — and they'll find things you never imagined.",
    body: `## The designer's indirect reach
Game design is a **second-order design problem**: you don't author the player's experience directly, you author the **rules**, and the experience emerges as players interact with them. You're designing the *space of possibilities*, then handing it over.

## Players are co-authors
Given any system, players will optimize it, exploit it, and discover strategies you never intended — the **metagame** (see *The Vocabulary of Balance*). This is a feature, not a bug: emergent strategy is much of the depth. But it means you must design for what players *will* do, not what you *hope* they do.

## Implications
- **Playtest relentlessly** — the gap between intended and actual play is only visible in real hands (see *Prototyping & Playtesting*).
- **Expect optimization** — if a boring strategy is also the strongest, players will grind it; make the fun path the strong path.
- **Design the possibility space**, then prune dominant or degenerate strategies that collapse it.
- **Embrace good emergence** — surprising-but-fair player inventions are a gift; protect them.

## Key takeaways
- You shape rules; players shape strategies — plan for the second.
- The fun path must also be the optimal path, or players won't take it.`,
    sources: [
      { title: "Advanced Game Design: A Systems Approach", author: "Michael Sellers" },
      { title: "Rules of Play: Game Design Fundamentals", author: "Katie Salen & Eric Zimmerman" },
    ],
    tags: ["systems", "emergence", "metagame"],
  },

  {
    id: "lib-beating-designers-block",
    disciplineIds: ["scoping", "systems"],
    title: "Beating Designer's Block",
    summary: "Constraint-driven ideation techniques: when you're stuck, change a rule, a resource, or a relationship.",
    body: `## Creativity loves constraints
A blank page is paralyzing; a **constraint** is generative. *Challenges for Game Designers* offers concrete moves to break a stuck design by deliberately perturbing it:
- **Limit (or unlimit) a resource** — what if ammo were infinite? what if you had exactly one bullet ever?
- **Kill a rule** — remove a rule you assumed was load-bearing and see what the game becomes.
- **Mess with the play order** — let players act simultaneously, or in reverse, or out of turn.
- **Rule of Two** — take a familiar mechanic and double, halve, or duplicate it.
- **Change who interacts** — turn a solo action cooperative, or a cooperative one competitive.

## Iterative by nature
These are **prototyping prompts**, not final answers. Apply one, build the cheapest test, and see whether the perturbation opened something interesting (see *Prototyping & Playtesting*). Most won't — but the few that do are where original designs come from.

## In practice
- When stuck, list your design's core assumptions, then break one on purpose.
- Generate many cheap variants; judge them by play, not by how clever they sound.

## Key takeaways
- Constraints generate ideas; a blank page doesn't.
- Break a load-bearing assumption and prototype what falls out.`,
    sources: [{ title: "Challenges for Game Designers", author: "Brenda Brathwaite & Ian Schreiber" }],
    tags: ["ideation", "process", "prototyping"],
  },

  {
    id: "lib-flow-conditions",
    disciplineIds: ["progression", "onboarding"],
    title: "The Conditions of Flow",
    summary: "Csikszentmihalyi's recipe for total absorption — and how games engineer it on purpose.",
    body: `## Beyond challenge-vs-skill
*Flow & Difficulty* covers the famous balance of challenge against skill. Csikszentmihalyi's full account lists several **conditions** that, together, produce the absorbed state of flow — and games are unusually good at supplying all of them at once:
- **Clear goals** — you always know what you're trying to do right now.
- **Immediate feedback** — every action returns a legible result, so you can adjust continuously.
- **Challenge matched to skill** — hard enough to engage, not so hard it triggers anxiety, nor so easy it bores.
- **A sense of control** — your actions matter and mistakes feel like yours to fix.
- **Merging of action and awareness** — doing and thinking fuse; the interface disappears.
- **Loss of self-consciousness** and **distorted time** — symptoms of deep absorption.
- **Autotelic experience** — the activity becomes its own reward.

## Designing for flow
- Keep a **clear immediate goal** visible at every moment; flow breaks when players don't know what to do.
- Make **feedback instant and unambiguous** (see *Measuring Game Feel*).
- Hold the player in the **flow channel** by ramping challenge with growing skill (see *Flow & Difficulty*).
- Protect control and presence — input lag, unfair deaths, and confusing UI all eject the player from flow.

## Key takeaways
- Flow needs clear goals + instant feedback + matched challenge + felt control.
- Games can deliver every flow condition simultaneously — squander none of them.`,
    sources: [
      { title: "Flow: The Psychology of Optimal Experience", author: "Mihaly Csikszentmihalyi" },
      { title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" },
    ],
    tags: ["flow", "engagement", "psychology"],
  },

  {
    id: "lib-three-cs",
    disciplineIds: ["controls", "camera"],
    title: "The Three Cs: Character, Camera, Control",
    summary: "Rogers' rule that the avatar, the camera, and the controls must be designed together — they make or break moment-to-moment play.",
    body: `## One inseparable trio
Scott Rogers frames the foundation of any action game as the **Three Cs** — **Character, Camera, Control** — which are so entangled they must be tuned as a single system. Get them right and everything else has a chance; get them wrong and no level or story can save the game.

## Character
The player's avatar (or cursor, or vehicle) — its size, abilities, animations, and how its **weight and responsiveness** feel (see *Measuring Game Feel*). The character defines what verbs the game is even about.

## Camera
What the player sees, and therefore what they can act on. The camera must **always frame what matters**, avoid nausea (smoothing, lookahead, sensible priorities), and suit the genre. A bad camera quietly ruins otherwise-good design — players blame "the controls" when the real culprit is framing.

## Control
The mapping from input to action: responsive, comfortable for long sessions, remappable, with the most-used verbs on the most-comfortable inputs. Control is where *intent* becomes *action*; latency or awkward mapping is felt instantly.

## Designing them together
- A change to one ripples to the others — a faster character needs a camera that pulls back and controls that stay readable at speed.
- Prototype the Three Cs **first**, before levels or content; they are the bedrock of game feel.

## Key takeaways
- Character, camera, and control are one system, not three features.
- Nail them before anything else — they decide whether the game feels good to touch.`,
    sources: [{ title: "Level Up! The Guide to Great Video Game Design", author: "Scott Rogers" }],
    tags: ["controls", "camera", "game-feel"],
  },

  {
    id: "lib-narrative-arc",
    disciplineIds: ["narrative-delivery", "narrative"],
    title: "Narrative Arc & Emotional Design",
    summary: "Lupton's view of design as storytelling: an inciting incident, a shaped arc, and an audience cast as the protagonist.",
    body: `## Design tells stories
Ellen Lupton argues that all design — not just games — is **storytelling**: it leads an audience through a shaped experience with a beginning, middle, and end, engineered to provoke emotion and action. The same narrative tools apply to a level, a UI flow, or an onboarding sequence.

## The shape
- **Inciting incident** — the hook that disrupts the ordinary and pulls the audience in (see *Interest Curves*).
- **Arc** — rising action through complication toward a climax, then resolution; experiences are *shaped*, not flat.
- **The audience as protagonist** — in interactive design the user is the hero of their own journey; you author the world and the obstacles, they supply the agency (see *Second-Order Design*).
- **Emotional journey** — plan the feelings, not just the events: where you want curiosity, tension, relief, triumph.

## Tools borrowed from story
- **Personas** — design for a specific protagonist with specific desires.
- **Storyboards** — sequence the experience visually before building it (kin to whiteboxing).
- **Co-creation** — leave deliberate gaps for the audience to fill, which deepens ownership.

## Key takeaways
- Every designed experience is a journey with an arc — shape it deliberately.
- Cast the player as protagonist and author their *emotional* path, not just the events.`,
    sources: [{ title: "O Design como Storytelling", author: "Ellen Lupton" }],
    tags: ["narrative", "emotion", "storytelling"],
  },

  {
    id: "lib-what-games-do",
    disciplineIds: ["metagame", "progression"],
    title: "What Games Do For Us",
    summary: "McGonigal on the distinct emotional rewards games provide — fiero, blissful productivity, and epic meaning — and the four traits behind them.",
    body: `## Games as emotion engines
Jane McGonigal reframes games as deliverers of genuine, hard-to-find positive experiences. Naming the emotions you're targeting (see *The Pleasures of Play*) helps you design *for* them:
- **Fiero** — the primal surge of triumph over adversity, fists-in-the-air after a hard-won victory. It requires real, surmountable challenge.
- **Blissful productivity** — the satisfaction of clear, meaningful work with visible progress; players will happily "work" in a game in ways they'd resist in life.
- **Social fabric & connection** — trust and bonds built through shared play.
- **Epic meaning** — being part of something awe-inspiringly large; a purpose bigger than the moment.

## The four traits of a (good) game
McGonigal distills games to four shared traits worth designing around:
- A **goal** that focuses attention and gives purpose.
- **Rules** that unleash creativity by removing the obvious paths.
- A **feedback system** that shows progress and promises the goal is achievable.
- **Voluntary participation** — players freely accept the goal, rules, and feedback, which makes the work feel like play.

## In practice
- Decide which emotions (fiero? connection? meaning?) your game exists to produce, then design the challenge and feedback that earn them.
- Protect **voluntariness** — the moment a game feels coerced (grind, guilt, FOMO), the magic inverts.

## Key takeaways
- Games supply specific emotions — fiero, blissful productivity, epic meaning — by design, not by accident.
- Goal + rules + feedback + voluntary buy-in is the engine; keep participation genuinely free.`,
    sources: [{ title: "Reality Is Broken", author: "Jane McGonigal" }],
    tags: ["motivation", "emotion", "psychology"],
  },

  {
    id: "lib-localization",
    disciplineIds: ["localization"],
    title: "Localization & Culturalization",
    summary: "Reaching a global audience is a design problem, not a translation afterthought — plan for it from the start.",
    body: `## More than translation
**Localization** adapts a game's language, UI, and content for another region; **culturalization** goes further, adjusting meaning so the game lands well in another culture (symbols, humor, taboos, history). Both are far cheaper when designed for early than retrofitted late.

## Build for it from day one
- **Externalize all text.** Keep strings in data, never baked into art or hardcoded; one image of text means re-arting every language.
- **Never concatenate sentences.** Stitching fragments ("You found " + item + "!") breaks in languages with different grammar, gender, and word order. Use whole templated strings.
- **Leave room to grow.** Translations often run ~30% longer than English; auto-size containers and avoid fixed-width text boxes.
- **Support different scripts and directions.** Right-to-left layouts, vertical text, and large character sets affect fonts and UI mirroring.
- **Don't bake in idioms** or culture-specific references that won't survive translation.

## Culturalization
Beyond words: colors, gestures, religious or political symbols, and content ratings carry different meanings across markets. What's neutral in one place can be offensive or illegal in another — review content per target region.

## In practice
- Audit the riskiest assumption: where did you assume English, LTR, or a fixed text length?
- Pseudo-localize early (swap in long, accented test strings) to surface layout breaks before real translation.

## Key takeaways
- Localization is an architecture decision made on day one, not a final step.
- Translate words; culturalize meaning.`,
    sources: [
      { title: "Fundamentals of Game Design", author: "Ernest Adams" },
    ],
    tags: ["localization", "i18n", "global"],
  },

  {
    id: "lib-clarity",
    disciplineIds: ["ux", "signposting"],
    title: "Clarity & the Currency of Attention",
    summary: "How understandable a feature is without extra explanation — and why every added element spends a finite attention budget.",
    body: `## What clarity is
**Clarity** is how readable and understandable a feature (or the whole game) is to the player *without* extra explanation. A clear design lets the player just play — experimenting and learning by doing rather than stopping to read a manual.

A few elements construct that understanding:

| Element | The question it answers |
| --- | --- |
| Player goal | What is the player supposed to achieve right now? |
| Player status | What is their current condition or progress? |
| Threats | What is dangerous, and how soon? |
| Discoverable | Can the player find this naturally by playing, without a tutorial? |
| Intuitive | Does it feel natural — understood without stopping to think hard? |

## The currency of attention
Human attention is **finite** — players can really focus on one thing at a time. Every element you add competes for that budget, so the more that is on screen, the *less* clear each individual thing becomes.

> [!INSIGHT] Communication debt
> Every feature you add costs work to communicate and quietly lowers the clarity of everything around it. Add many things and they must all stay shallow; add fewer and each can be deep and nuanced. Clarity is the budget that decides how much your game can hold.

## Spend intensity only where it matters
A practical test: convert a busy screen to **grayscale**. Pure value and contrast reveal where the eye is actually pulled. If a decorative background out-contrasts the incoming threat, your priorities are inverted — raise intensity on what matters and quiet the rest.

> [!EXAMPLE] Attention, well spent vs. wasted
> Compare a clean competitive game (each element placed, calm palette, intensity reserved for the genuinely dangerous moment) against a noisy one (saturated effects everywhere, no focal point). In grayscale the clean game shows one clear hotspot where the danger is; the noisy one shows visual chaos with no priority. Same information, opposite clarity.

## Key takeaways
- Clarity lets players learn by doing instead of reading.
- Attention is finite; every feature spends from the same budget.
- Reserve visual intensity for what genuinely matters — test it in grayscale.`,
    sources: [
      { title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" },
      { title: "Designing Games", author: "Tynan Sylvester" },
    ],
    tags: ["clarity", "readability", "attention"],
  },
  {
    id: "lib-ww-possibility-space",
    disciplineIds: ["systems"],
    title: "Possibility Space & the Anatomy of a Game System",
    summary: "A game isn't a script — it's a space of possibilities built from agents, networks, and layers.",
    body: `## Games are systems, not scripts
A game is an interactive system the player operates. Instead of authoring a single path, you author a **space of possibilities** and let the player's choices trace their own route through it. Even a single chess piece sits at the root of a branching tree of futures — that tree *is* the game.

## Three building blocks
- **Agents** — the objects that populate the system, each with its own properties and behaviors. Make them readable, even empathetic, so players can predict them.
- **Networks** — the pathways agents move along. Keep them legible so the player can see which moves are available.
- **Layers** — the global rules that govern everything at once. A very small rules layer can produce enormous, unpredictable behavior.

## Stable vs. chaotic
- A **stable** system barely changes when inputs change — predictable, but risks boredom.
- A **chaotic** system swings wildly on small changes — surprising, but risks making the player feel out of control.

Tune between the two: enough determinism to reward planning, enough surprise to stay fresh.

## Shaping the space
- Recognizable dynamics — **growth, allocation, destruction, evolution** — each carry their own emotions. Theme them with a real-world metaphor (a wildfire for destruction, migration for allocation) so players read them instantly.
- Two ways to structure a possibility space: **gated** (wide exploration between checkpoints) and **branching** (distinct forks toward different outcomes).

> [!TIP] When balancing, change variables *dramatically*, not in tiny steps. Push the system to its extremes to learn what's possible, then pull it back until it's fun. The edges tell you where the good middle is.

## Key takeaways
- Design the space of moves, not the single intended move.
- Agents, networks, and layers are the knobs; emergence is the payoff.
- Find fun at the extremes first, then dial inward.`,
    sources: [
      { title: "Game Design and Theory", author: "Will Wright (MasterClass)" },
      { title: "Thinking in Systems: A Primer", author: "Donella H. Meadows" },
    ],
    tags: ["systems", "emergence", "possibility-space"],
  },
  {
    id: "lib-ww-mental-model",
    disciplineIds: ["progression"],
    title: "The Player's Mental Model",
    summary: "Designing a game is as much about programming the player's mind as the software.",
    body: `## You're programming two machines
Building a game is as much about programming the **player's mind** as the software. A compelling design installs a temporary interior system of goals, limits, and rewards that, for the length of play, can override the player's real-world motivations.

## Schemas and expectations
Players arrive with **schemas** — mental frameworks built from other games and from life — and they read your game through them. Know the expectations your game sets so you can steer attention toward its most rewarding parts instead of fighting the player's assumptions.

## Build motivation deliberately
The Sims structured its objectives on a needs hierarchy (in the spirit of Maslow), giving players a legible ladder of wants to satisfy. Your model doesn't have to obey reality — only pure simulators chase accuracy. Make systems responsive, whimsical, even humorous.

## Design toward the roles players invent
Watch the roles that emerge in your community — collectors, storytellers, toolmakers — and design to support them (add a rarity structure for collectors, sharing tools for creators). Players who forge their own identity inside your game become its loudest evangelists.

## Key takeaways
- A great game replaces real-world motivation with an interior one, for a while.
- Meet the player's existing schemas, then redirect them.
- Support the roles players create; don't only dictate behavior.`,
    sources: [
      { title: "Game Design and Theory", author: "Will Wright (MasterClass)" },
      { title: "The Design of Everyday Things", author: "Don Norman" },
    ],
    tags: ["psychology", "motivation", "mental-model"],
  },
  {
    id: "lib-ww-game-language",
    disciplineIds: ["signposting"],
    title: "Game Language: Nouns, Verbs & Adjectives",
    summary: "Every game teaches a private grammar of signs; fluency lets you communicate rules without a manual.",
    body: `## Games speak in signs
Every game teaches the player a private language of signs and symbols. Getting fluent in that grammar lets you communicate rules without a manual.

## The parts of speech
- **Nouns** — the interactable objects and characters that populate the world.
- **Verbs** — the actions available to the player.
- **Adjectives** — the subjective character of the nouns (a *scary* enemy, a *cute* one) that colors how it feels to interact with them.

## Directing behavior with language
- Foreground your nouns with **color saturation** and motion; use **animation** to advertise which verbs are available. This is *affordance* — the interactions a player can see and understand at a glance.
- Adjectives shape choices before a single rule is read: a player flees an *angry* dog but approaches a *friendly* one.

## Lean on metaphor
Common metaphors and classic archetypes carry a ready-made bundle of assumptions. If a dragon carries off the mayor, you don't need to explain the quest — the player already knows to hunt the dragon. Reference the familiar to bootstrap understanding, then subvert it where the subversion earns you something.

## Key takeaways
- Teach rules through nouns, verbs, and adjectives — not text boxes.
- Affordances should be visible and legible; make verbs *look* like verbs.
- Metaphor is a shortcut straight into the player's expectations.`,
    sources: [
      { title: "Game Design and Theory", author: "Will Wright (MasterClass)" },
      { title: "The Design of Everyday Things", author: "Don Norman" },
    ],
    tags: ["game-language", "affordance", "communication"],
  },
  {
    id: "lib-ww-triage-scoping",
    disciplineIds: ["scoping"],
    title: "Iteration, Local Maxima & Feature Triage",
    summary: "Design is hill-climbing: escape local maxima, then triage features on value × cost.",
    body: `## Design is hill-climbing
Picture your design as a point on a landscape of quality. Iterating pushes you uphill toward a **local maximum** — the best version of *this* idea. The trap: once you're on top of a small hill, more polish stops helping.

## Escaping a local maximum
To reach a taller hill you first have to climb *down* — a dramatic change of direction, often ripping out a core feature or breaking a beloved prototype. The best designs cross this valley two or three times before they're as good as they can be. And a prototype that doesn't fit today can seed a future game (Will's city-building tool became SimCity).

## Feature triage
Late in production, scope with a two-number method: designers assign each wanted feature a **design value** (how much it serves the core experience); engineers assign an **engineering cost** (how long it takes). Read the two together.

| Design value | Engineering cost | Verdict |
| --- | --- | --- |
| High | Low | Critical — build first |
| High | High | Negotiate or slice it down |
| Low | Low | Non-critical — nice to have |
| Low | High | Out of scope — cut it |

## Scope as discipline
Scope is the balancing act between what's cool and what's possible. Be ruthless, but never lose the original vision, and trust your instincts about what is fun.

## Key takeaways
- Local maxima are real; escaping them means temporary regressions.
- Triage on value × cost, not on enthusiasm.
- Cut features, keep the vision.`,
    sources: [
      { title: "Game Design and Theory", author: "Will Wright (MasterClass)" },
      { title: "Blood, Sweat, and Pixels", author: "Jason Schreier" },
    ],
    tags: ["scoping", "iteration", "feature-triage"],
  },
  {
    id: "lib-ww-design-docs",
    disciplineIds: ["scoping"],
    title: "Design Documents: One-Sheet to Full GDD",
    summary: "The GDD is your project's living memory; scale the same ideas from a one-sheet up to the full document.",
    body: `## The GDD is "living memory"
No design should live and die in your head. A **Game Design Document** is the living memory of the project — you write in it constantly, starting early, deleting sections that don't apply and adding ones that do. It grows long and messy; keep a tidy, presentable version to share.

## A ladder of documents
Different audiences and stages need different depths. Scale the same ideas up or down:

| Document | Length | Purpose |
| --- | --- | --- |
| **One-Sheet** | 1 page | The hook: title, logline, key features, mood, audience, one image |
| **Ten-Page** | ~10 pages | The treatment: player experience, core mechanics, systems, art & sound direction |
| **Pitch** | Deck / brief | Audience-shaped persuasion, led by the player's experience |
| **Technical GDD** | Varies | The build view: platform, engine, saved data, architecture, revenue plumbing |
| **Small GDD** | Working subset | Gameplay + core mechanics + art/sound + production essentials |
| **Full GDD** | 30+ pages | Complete reference across gameplay, technical, art, sound, narrative, production |

## Three bodies of work behind it
A GDD is fed by two companions: a **concept book** (high-level ideas and responses to thought experiments) and a **prototype library** (playable experiments, each with a cover sheet recording the design question it asked and the lesson it taught).

## Key takeaways
- Write it early, write it often — it's memory, not paperwork.
- Pick the document depth to match the audience and the stage.
- Feed the doc from your concept book and prototype library.`,
    sources: [
      { title: "Game Design and Theory", author: "Will Wright (MasterClass)" },
      { title: "The Art of Game Design: A Book of Lenses", author: "Jesse Schell" },
    ],
    tags: ["gdd", "documentation", "production"],
  },
  {
    id: "lib-ww-pitch-logline",
    disciplineIds: ["scoping"],
    title: "Pitching & the Logline",
    summary: "The first game you design is the pitch — sell the experience, not the genre.",
    body: `## The first game you design is the pitch
A pitch is a concise sell of your experience to a specific audience — and it's the first "game" you design, because it builds the game in the listener's head before a line of code exists.

## Pitch from the player's seat
Don't lead with a genre or a comparison. Describe the **feelings** the player will have, the control they'll hold, the interactions available to them. Get the audience playing the game in their imagination while you talk.

## One size fits none
Tailor the message to who's listening:
- **Team** — what they'd be proud to build.
- **Investors** — why it will succeed.
- **Press** — why it's fresh and new.

Change the framing, never the enthusiasm.

## The logline
The **logline** is one or two sentences capturing the core experience — the strongest line in the pitch and the first thing in your marketing. Written well, it seeds a mental model of how the game plays. Draft it from the player's perspective with clear nouns, strong verbs, and evocative adjectives; build a small model of the game in the reader's mind, then cut until one sentence remains.

## Key takeaways
- Sell the experience, not the category.
- Re-shape the pitch per audience; keep the passion constant.
- A logline is a compressed mental model — make every word carry.`,
    sources: [
      { title: "Game Design and Theory", author: "Will Wright (MasterClass)" },
      { title: "Blood, Sweat, and Pixels", author: "Jason Schreier" },
    ],
    tags: ["pitching", "logline", "communication"],
  },
];

// ---- Compilation into a book (table of contents) ----

export interface BookChapter {
  discipline: Category;
  entries: LibraryEntry[];
}
export interface BookGroup {
  group: string;
  chapters: BookChapter[];
}

/** Flatten entries into their reading order (discipline taxonomy order). */
export function orderedEntries(entries: LibraryEntry[] = LIBRARY_ENTRIES): LibraryEntry[] {
  const out: LibraryEntry[] = [];
  for (const cat of CATEGORIES) {
    for (const e of entries) {
      if (e.disciplineIds[0] === cat.id) out.push(e);
    }
  }
  // Include any entries whose primary discipline isn't in the taxonomy (safety).
  for (const e of entries) if (!out.includes(e)) out.push(e);
  return out;
}

/** Group entries into Group → Discipline chapters for the table of contents. */
export function buildBook(entries: LibraryEntry[] = LIBRARY_ENTRIES): BookGroup[] {
  const groups: BookGroup[] = [];
  for (const cat of CATEGORIES) {
    const catEntries = entries.filter((e) => e.disciplineIds[0] === cat.id);
    if (catEntries.length === 0) continue;
    let group = groups.find((g) => g.group === cat.group);
    if (!group) {
      group = { group: cat.group, chapters: [] };
      groups.push(group);
    }
    group.chapters.push({ discipline: cat, entries: catEntries });
  }
  return groups;
}

export function searchEntries(query: string): LibraryEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return LIBRARY_ENTRIES;
  return LIBRARY_ENTRIES.filter((e) => {
    const hay = [
      e.title,
      e.summary,
      e.body,
      e.tags.join(" "),
      e.disciplineIds.map((id) => CATEGORY_BY_ID[id]?.name ?? "").join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

export const ENTRY_BY_ID: Record<string, LibraryEntry> = Object.fromEntries(
  LIBRARY_ENTRIES.map((e) => [e.id, e])
);

/** Library entries relevant to a set of disciplines (for cross-links). */
export function entriesForDiscipline(disciplineIds: string[]): LibraryEntry[] {
  const set = new Set(disciplineIds);
  return LIBRARY_ENTRIES.filter((e) => e.disciplineIds.some((id) => set.has(id)));
}

/** Other entries sharing a discipline (for "See also"). */
export function relatedEntries(entry: LibraryEntry, limit = 4): LibraryEntry[] {
  const set = new Set(entry.disciplineIds);
  return LIBRARY_ENTRIES.filter(
    (e) => e.id !== entry.id && e.disciplineIds.some((id) => set.has(id))
  ).slice(0, limit);
}

/** All distinct tags across the library. */
export function allTags(): string[] {
  const s = new Set<string>();
  LIBRARY_ENTRIES.forEach((e) => e.tags.forEach((t) => s.add(t)));
  return [...s].sort();
}

/** Rough reading-time estimate in minutes (~200 wpm). */
export function estReadMinutes(entry: LibraryEntry): number {
  const words = entry.body.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

// ---- Session tree (Main Session → Sub Session) ----
// The Library reads as a continuous book organized into user-editable sessions.
// Built-in entries seed a default tree (Main Session = discipline group, Sub
// Session = the entry); the discipline is kept as a hidden practice link.

export const GROUP_ORDER = ["Design", "Craft", "Production", "Business"];

export interface MainSession {
  id: string;
  title: string;
  order: number;
}
export interface SubSession {
  id: string;
  mainId: string;
  title: string;
  body: string;
  /** Optional discipline link, kept out of the reading flow (powers Practice). */
  disciplineId?: string;
  sources: LibrarySource[];
  tags: string[];
  order: number;
}
export interface SessionTree {
  mainSessions: MainSession[];
  subSessions: SubSession[];
}

export function groupOfDiscipline(id?: string): string {
  return (id && CATEGORY_BY_ID[id]?.group) || "Design";
}
export function mainIdForGroup(group: string): string {
  return `main-${group.toLowerCase().replace(/\s+/g, "-")}`;
}

/** The default session tree derived from the built-in entries (migration seed). */
export function buildSeedSessionTree(): SessionTree {
  const groupsPresent = GROUP_ORDER.filter((g) =>
    orderedEntries().some((e) => groupOfDiscipline(e.disciplineIds[0]) === g)
  );
  const mainSessions: MainSession[] = groupsPresent.map((g, i) => ({
    id: mainIdForGroup(g),
    title: g,
    order: i,
  }));
  const perMainCount: Record<string, number> = {};
  const subSessions: SubSession[] = orderedEntries().map((e) => {
    const group = groupOfDiscipline(e.disciplineIds[0]);
    const mainId = mainIdForGroup(group);
    const order = perMainCount[mainId] ?? 0;
    perMainCount[mainId] = order + 1;
    return {
      id: e.id,
      mainId,
      title: e.title,
      body: e.body,
      disciplineId: e.disciplineIds[0],
      sources: e.sources,
      tags: e.tags,
      order,
    };
  });
  return { mainSessions, subSessions };
}
